import { X } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
  formatAssetCode,
  MAX_CODE_PREFIX_LENGTH,
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type Role,
} from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/cargofl-ui';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { EmployeePicker } from '@/components/pickers';
import { PageContainer } from '@/components/shell/page-container';
import { Hint } from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useSettings, useUpdateSettings } from '@/lib/admin-api';
import {
  useAssetCategories,
  useCreateCategory,
  useDeleteCategory,
  useSetCategoryCredentialRequirement,
  type AssetCategory,
} from '@/lib/assets-api';
import { useChangeRole, useEmployees } from '@/lib/employees-api';
import { useCurrentUser } from '@/lib/session';

export function SettingsPage() {
  return (
    <PageContainer>
      <CodeSequenceCard />
      <CategoriesCard />
      <DeviceLoginCard />
      <RolesCard />
    </PageContainer>
  );
}

function CodeSequenceCard() {
  const { data: settings } = useSettings();
  const update = useUpdateSettings();
  const [prefix, setPrefix] = React.useState('');
  const [employeePrefix, setEmployeePrefix] = React.useState('');
  const [digits, setDigits] = React.useState('');
  const [sequence, setSequence] = React.useState('');
  const [employeeSequence, setEmployeeSequence] = React.useState('');

  React.useEffect(() => {
    if (settings) {
      setPrefix(settings.codePrefix);
      setEmployeePrefix(settings.employeeCodePrefix);
      setDigits(String(settings.codeDigits));
      setSequence(String(settings.nextSequence));
      setEmployeeSequence(String(settings.employeeNextSequence));
    }
  }, [settings]);

  // Previewed from what is TYPED, not from what is stored: the point of the preview is
  // to show the effect of a prefix change before Save, and reading it back off the
  // server meant the employee line still showed the old prefix while you edited it.
  const preview = (value: string, next: number | undefined) =>
    settings && value.trim() && next !== undefined && Number.isFinite(next)
      ? formatAssetCode(next, {
          prefix: value.trim().toUpperCase(),
          digits: Number(digits) || settings.codeDigits,
        })
      : '—';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await update.mutateAsync({
        codePrefix: prefix.trim().toUpperCase(),
        employeeCodePrefix: employeePrefix.trim().toUpperCase(),
        codeDigits: Number(digits),
        nextSequence: Number(sequence),
        employeeNextSequence: Number(employeeSequence),
      });
      toast.success('Asset code format saved.');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be saved.');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Asset codes</CardTitle>
        <CardDescription className="text-xs">
          The code printed on the sticker. Changing the format affects assets registered
          from now on — existing codes never change, because they are already on labels and
          in spreadsheets. An employee-owned asset takes the second prefix, so a personal
          device is recognisable from its code alone.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Company-owned prefix</Label>
              <Input
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                maxLength={MAX_CODE_PREFIX_LENGTH}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Employee-owned prefix</Label>
              <Input
                value={employeePrefix}
                onChange={(e) => setEmployeePrefix(e.target.value)}
                maxLength={MAX_CODE_PREFIX_LENGTH}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Digits</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={digits}
                onChange={(e) => setDigits(e.target.value)}
              />
            </div>
          </div>

          {/* The counter is editable, and the code it will mint sits directly under it.
              Two numbers rather than one because each series is counted separately. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Next company-owned number</Label>
              <Input
                type="number"
                min={1}
                className="at-num"
                value={sequence}
                onChange={(e) => setSequence(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Next code <span className="at-num font-medium text-foreground">{preview(prefix, Number(sequence))}</span>
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Next employee-owned number</Label>
              <Input
                type="number"
                min={1}
                className="at-num"
                value={employeeSequence}
                onChange={(e) => setEmployeeSequence(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Next code{' '}
                <span className="at-num font-medium text-foreground">
                  {preview(employeePrefix, Number(employeeSequence))}
                </span>
              </p>
            </div>
          </div>

          <Alert>
            <AlertDescription className="text-xs">
              Either counter can be moved in either direction, as long as no asset already
              carries a code the register would reach again — forward to leave a block free
              for kit bought elsewhere, back to undo a counter that ran away during a trial
              run. If a code is in the way the API names it and refuses, because minting it
              twice would put one sticker number on two assets.
            </AlertDescription>
          </Alert>

          <Button hint={"Save these settings — they apply to everyone"} type="submit" size="sm" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save format'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function CategoriesCard() {
  const { data: categories } = useAssetCategories();
  const create = useCreateCategory();
  const remove = useDeleteCategory();
  const [name, setName] = React.useState('');
  const [pending, setPending] = React.useState<AssetCategory | null>(null);

  /**
   * A type still on assets cannot go - those rows would be left with no type at all.
   *
   * Said here as well as by the API so it lands on the click rather than after a round
   * trip, and so the × is present on every chip: an × that vanishes on the types you
   * cannot delete looks like a missing feature, where one that explains itself teaches
   * the rule. The API's refusal is the real control.
   */
  const askRemove = (category: AssetCategory) => {
    // Always present here: this screen is manageSettings, which implies viewRegister.
    const n = category._count?.assets ?? 0;
    if (n > 0) {
      toast.error(
        `${category.name} is the type on ${n} asset${n === 1 ? '' : 's'}. Move ${
          n === 1 ? 'it' : 'them'
        } to another type first, then remove ${category.name}.`,
      );
      return;
    }
    setPending(category);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      await create.mutateAsync({ name: name.trim() });
      toast.success(`${name.trim()} added.`);
      setName('');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be added.');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Asset types</CardTitle>
        <CardDescription className="text-xs">
          What the type dropdown offers. A bulk import creates any type it does not
          recognise, so this list fills itself out over time. The number is how many
          assets carry it; × removes one nothing is registered under.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {categories?.map((category) => (
            <Badge key={category.id} variant="soft-muted" className="rounded-full px-2.5 py-0.5">
              {category.name}
              <span className="at-num ml-1.5 opacity-60">{category._count?.assets ?? 0}</span>
              <Hint label={`Remove ${category.name}`}><button
                type="button"
                aria-label={`Remove ${category.name}`}
                disabled={remove.isPending}
                onClick={() => askRemove(category)}
                className="ml-1 rounded-full opacity-40 transition-opacity hover:opacity-100 focus-visible:opacity-100 disabled:opacity-20"
              >
                <X className="size-3" />
              </button></Hint>
            </Badge>
          ))}
        </div>

        <form onSubmit={submit} className="flex gap-2">
          <Input
            className="max-w-xs"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Docking station"
          />
          <Button hint={"Add an asset type (laptop, monitor, chair…)"} type="submit" size="sm" variant="secondary" disabled={create.isPending}>
            Add type
          </Button>
        </form>

        <ConfirmDialog
          open={pending !== null}
          onOpenChange={(open) => !open && setPending(null)}
          title={`Remove ${pending?.name ?? ''}?`}
          description="It stops being offered in the type dropdown."
          consequence="No asset is registered under it, so nothing else changes. Add it again if you need it back."
          confirmLabel="Remove type"
          destructive
          onConfirm={() => remove.mutateAsync(pending!.id)}
          successMessage={`${pending?.name ?? 'Type'} removed.`}
        />
      </CardContent>
    </Card>
  );
}

/**
 * Who may do what.
 *
 * The role list is shown with its full description rather than a bare name, because
 * IT and HR are peers with different scopes rather than rungs on a ladder - "HR" does
 * not obviously exclude registering a laptop unless somebody says so.
 */
function RolesCard() {
  const me = useCurrentUser();
  const { data } = useEmployees({ pageSize: 1000 });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Roles</CardTitle>
        <CardDescription className="text-xs">
          Changing someone’s role signs them out everywhere immediately, so a reduced role
          takes effect at once rather than whenever their session happens to expire.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {ROLES.map((role) => (
            <div key={role} className="rounded-md border p-2.5">
              <div className="text-xs font-semibold">{ROLE_LABELS[role]}</div>
              <div className="text-[11px] leading-relaxed text-muted-foreground">
                {ROLE_DESCRIPTIONS[role]}
              </div>
            </div>
          ))}
        </div>

        <div className="divide-y rounded-md border">
          {data?.employees
            .filter((employee) => employee.role !== 'EMPLOYEE')
            .map((employee) => (
              <RoleRow
                key={employee.id}
                id={employee.id}
                name={employee.name}
                code={employee.employeeCode}
                role={employee.role}
                isSelf={employee.id === me.id}
              />
            ))}
          <div className="p-3">
            <p className="mb-2 text-xs text-muted-foreground">
              Grant a role to someone else:
            </p>
            <GrantRole employees={data?.employees.filter((e) => e.role === 'EMPLOYEE') ?? []} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RoleRow({
  id,
  name,
  code,
  role,
  isSelf,
}: {
  id: string;
  name: string;
  code: string;
  role: Role;
  isSelf: boolean;
}) {
  const changeRole = useChangeRole(id);
  // Held rather than applied: the Select is one stray click away from signing
  // somebody out of every session they have.
  const [pending, setPending] = React.useState<Role | null>(null);

  return (
    <div className="flex flex-wrap items-center gap-3 p-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">
          {name}
          {isSelf && <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>}
        </div>
        <div className="at-num text-xs text-muted-foreground">{code}</div>
      </div>
      <Select value={role} onValueChange={(value) => setPending(value as Role)}>
        <SelectTrigger className="w-[210px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ROLES.map((r) => (
            <SelectItem key={r} value={r}>
              {ROLE_LABELS[r]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={`Make ${name} ${pending ? ROLE_LABELS[pending] : ''}?`}
        description={`${code} is currently ${ROLE_LABELS[role]}.`}
        consequence={
          <>
            This signs {isSelf ? 'you' : name} out everywhere immediately, so the new role
            takes effect at once rather than whenever the session happens to expire.
            {isSelf && ' You will have to sign in again.'}
          </>
        }
        confirmLabel="Change role"
        destructive={isSelf}
        successMessage={`${name} is now ${pending ? ROLE_LABELS[pending] : ''}.`}
        onConfirm={() => changeRole.mutateAsync({ role: pending! })}
      />
    </div>
  );
}

function GrantRole({
  employees,
}: {
  employees: Array<{ id: string; name: string; employeeCode: string }>;
}) {
  const [employeeId, setEmployeeId] = React.useState('');
  const [role, setRole] = React.useState<Role>('IT');
  const changeRole = useChangeRole(employeeId);

  const grant = () => {
    if (!employeeId) return;
    const person = employees.find((e) => e.id === employeeId);
    void changeRole
      .mutateAsync({ role })
      .then(() => {
        toast.success(`${person?.name ?? 'They'} are now ${ROLE_LABELS[role]}.`);
        setEmployeeId('');
      })
      .catch((error: unknown) =>
        toast.error(error instanceof ApiError ? error.message : 'That did not work.'),
      );
  };

  return (
    <div className="flex flex-wrap gap-2">
      <div className="w-[260px]">
        <EmployeePicker employees={employees} value={employeeId} onChange={setEmployeeId} />
      </div>

      <Select value={role} onValueChange={(v) => setRole(v as Role)}>
        <SelectTrigger className="w-[210px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ROLES.filter((r) => r !== 'EMPLOYEE').map((r) => (
            <SelectItem key={r} value={r}>
              {ROLE_LABELS[r]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button hint={"Give this person the chosen access"} size="sm" variant="secondary" disabled={!employeeId} onClick={grant}>
        Grant
      </Button>
      {!employeeId && <span className="self-center text-xs text-muted-foreground">Choose a person first</span>}
    </div>
  );
}

/**
 * Which asset types have to surrender the machine's login when they come back.
 *
 * Chips rather than nineteen switches: the answer is yes for two types and no for
 * everything else, so the useful shape is "here is the short list, tap to change it"
 * rather than a wall of controls that are all off.
 *
 * Deliberately not per asset. It is a fact about the KIND of thing - a laptop has a
 * local account, a chair does not - and a question asked four hundred times is one
 * answered carelessly.
 */
function DeviceLoginCard() {
  const { data: categories } = useAssetCategories();
  const setRequirement = useSetCategoryCredentialRequirement();

  const toggle = async (category: AssetCategory) => {
    try {
      await setRequirement.mutateAsync({
        id: category.id,
        requiresCredentials: !category.requiresCredentials,
      });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be changed.');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Device login on return</CardTitle>
        <CardDescription className="text-xs">
          Returning one of these types asks for the account name and password on the machine
          itself, so IT can start it up and check it before it goes out again. It is stored
          encrypted, anyone who looks at it later is named in the audit log, and it can be
          deleted once the machine is wiped. Never asked for an employee&rsquo;s own device.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1.5">
          {categories?.map((category) => (
            <button
              key={category.id}
              type="button"
              aria-pressed={category.requiresCredentials}
              disabled={setRequirement.isPending}
              onClick={() => toggle(category)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-50',
                category.requiresCredentials
                  ? 'border-primary bg-primary/10 font-medium text-primary'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {category.name}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
